import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

// =============================================================================
// Types
// =============================================================================

export type OrderStatus =
  | "pending"
  | "accepted"
  | "in_progress"
  | "ready"
  | "delivered"
  | "rejected"
  | "cancelled";

export type OrderPaymentStatus = "unpaid" | "pending" | "paid" | "refunded" | "failed";

export type OrderPaymentMode = "pay_now" | "pay_later";

export type RestaurantTableSummary = {
  id: string;
  label: string;
};

export type DeliveryState =
  | "unassigned"
  | "assigned"
  | "accepted"
  | "en_route"
  | "arrived"
  | "delivered"
  | "failed"
  | "cancelled";

export type DeliverySummary = {
  id: string;
  state: DeliveryState;
  riderId: string | null;
  riderName: string | null;
  enRouteAt: string | null;
  arrivedAt: string | null;
  deliveredAt: string | null;
};

export type Order = {
  id: string;
  organizationId: string;
  restaurantTableId: string | null;
  guestSessionId: string | null;
  userId: string | null;
  dailyNumber: number;
  dailyDate: string;
  origin: string;
  fulfillmentType: "dine_in" | "takeaway" | "delivery" | string;
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  paymentMode: OrderPaymentMode;
  paymentProvider: string | null;
  paymentIntentId: string | null;
  currency: string;
  subtotal: number;
  discountTotal: number;
  deliveryFee: number;
  total: number;
  notes: string | null;
  contactName: string | null;
  contactPhone: string | null;
  customerAddressId: string | null;
  deliverySummary: DeliverySummary | null;
  rejectionReason: string | null;
  acceptedAt: string | null;
  startedAt: string | null;
  readyAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  updatedAt: string;
  restaurantTable: RestaurantTableSummary | null;
};

export type OrderItem = {
  id: string;
  orderId: string;
  productId: string | null;
  nameSnapshot: string;
  priceSnapshot: number;
  quantity: number;
  notes: string | null;
};

export type OrderWithItems = Order & { items: OrderItem[] };

export type OrderListResponse = { orders: OrderWithItems[] };
export type OrderDetailResponse = { order: Order; items: OrderItem[] };

export type OrderHistoryItem = {
  id: string;
  dailyNumber: number;
  organizationId: string;
  organizationName: string;
  organizationSlug: string | null;
  fulfillmentType: "dine_in" | "takeaway" | "delivery" | string;
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  total: number;
  currency: string;
  createdAt: string;
  deliveredAt: string | null;
};

export type OrderHistoryResponse = { orders: OrderHistoryItem[] };

export type CreateOrderInput = {
  orgSlug?: string;
  items: { productId: string; quantity: number; notes?: string | null }[];
  paymentMode: OrderPaymentMode;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
};

export type CreateOrderResponse = {
  order: Pick<
    Order,
    | "id"
    | "dailyNumber"
    | "dailyDate"
    | "status"
    | "paymentStatus"
    | "paymentMode"
    | "currency"
    | "subtotal"
    | "discountTotal"
    | "total"
  >;
  checkoutUrl: string | null;
};

export type GuestSessionResponse = {
  sessionId: string;
  tableLabel: string;
};

// =============================================================================
// Queries
// =============================================================================

export function useOrders(filters?: {
  status?: OrderStatus[];
  from?: string;
  includeDeliveryBoard?: boolean;
  to?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: queryKeys.orders.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status?.length) {
        params.set("status", filters.status.join(","));
      }
      if (filters?.from) {
        params.set("from", filters.from);
      }
      if (filters?.to) {
        params.set("to", filters.to);
      }
      if (filters?.includeDeliveryBoard) {
        params.set("includeDeliveryBoard", "true");
      }
      if (filters?.limit) {
        params.set("limit", String(filters.limit));
      }
      const qs = params.toString();
      return await requestJson<OrderListResponse>(`/api/orders${qs ? `?${qs}` : ""}`);
    },
    refetchInterval: 30_000,
  });
}

export function useOrder(orderId?: string) {
  return useQuery({
    queryKey: queryKeys.orders.detail(orderId),
    enabled: Boolean(orderId),
    queryFn: async () => await requestJson<OrderDetailResponse>(`/api/orders/${orderId}`),
  });
}

export function useOrderHistory() {
  return useQuery({
    queryKey: queryKeys.orders.mine(),
    queryFn: async () => await requestJson<OrderHistoryResponse>("/api/orders/mine"),
  });
}

// =============================================================================
// Guest mutations
// =============================================================================

export function useStartGuestSession() {
  return useMutation({
    mutationFn: async (input: { orgSlug: string; qrToken: string }) =>
      await requestJson<GuestSessionResponse>("/api/orders/guest-session", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(input),
      }),
  });
}

export function useCreateOrder() {
  return useMutation({
    mutationFn: async (input: CreateOrderInput) =>
      await requestJson<CreateOrderResponse>("/api/orders", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(input),
      }),
  });
}

// =============================================================================
// Staff mutations (transitions)
// =============================================================================

type Transition = "accept" | "start" | "ready" | "deliver" | "cancel" | "reject" | "mark-paid";

export function useTransitionOrder(transition: Transition) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; reason?: string }) => {
      const body =
        transition === "reject" && input.reason
          ? JSON.stringify({ reason: input.reason })
          : undefined;
      const response = await requestJson<{ order: Order }>(
        `/api/orders/${input.orderId}/${transition}`,
        {
          method: "POST",
          headers: body ? JSON_HEADERS : undefined,
          body,
        },
      );
      return response.order;
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.orders.detail(variables.orderId),
        }),
      ]);
    },
  });
}

// =============================================================================
// Settings + credentials
// =============================================================================

export type DeliveryOriginSettings = {
  lat: string | number | null;
  lng: string | number | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  country: string | null;
  configuredAt: string | null;
};

export type DeliveryCoverageSettings = {
  radiusM: number | null;
  configuredAt: string | null;
};

export type OrderingSettingsResponse = {
  orderingEnabled: boolean;
  deliveryEnabled: boolean;
  cashOnDeliveryEnabled: boolean;
  deliveryFeeFlat: number;
  deliveryMinimumOrder: number;
  deliveryOrigin: DeliveryOriginSettings | null;
  deliveryCoverage: DeliveryCoverageSettings | null;
  credentials: {
    provider: "mercado_pago";
    isLive: boolean;
    publicKey: string | null;
    userIdAtProvider: string | null;
    configuredAt: string | null;
    verifiedAt: string | null;
  } | null;
};

export type DeliveryOriginPatch = {
  lat: number;
  lng: number;
  line1: string;
  line2?: string | null;
  city?: string | null;
  country?: string;
};

export type DeliveryCoveragePatch = {
  radiusM: number;
};

export type OrderingSettingsPatch = {
  orderingEnabled?: boolean;
  deliveryEnabled?: boolean;
  cashOnDeliveryEnabled?: boolean;
  /** Flat Delivery fee, in the same units as the rest of Order pricing. */
  deliveryFeeFlat?: number;
  /** Delivery minimum order, in the same units as the rest of Order pricing. */
  deliveryMinimumOrder?: number;
  deliveryOrigin?: DeliveryOriginPatch | null;
  deliveryCoverage?: DeliveryCoveragePatch | null;
  clearDeliveryOrigin?: boolean;
  clearDeliveryCoverage?: boolean;
};

export function useOrderingSettings() {
  return useQuery({
    queryKey: queryKeys.orders.settings(),
    queryFn: async () => await requestJson<OrderingSettingsResponse>("/api/orders/settings"),
  });
}

export function useUpdateOrderingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrderingSettingsPatch) =>
      await requestJson<OrderingSettingsResponse>("/api/orders/settings", {
        method: "PATCH",
        headers: JSON_HEADERS,
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.settings() });
    },
  });
}

export function useUpsertPaymentCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { accessToken: string; publicKey?: string | null }) =>
      await requestJson<OrderingSettingsResponse>("/api/orders/payment-credentials", {
        method: "PUT",
        headers: JSON_HEADERS,
        body: JSON.stringify({ provider: "mercado_pago", ...input }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.settings() });
    },
  });
}

export function useDeletePaymentCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      await requestJson<{ success: boolean }>("/api/orders/payment-credentials/mercado_pago", {
        method: "DELETE",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.settings() });
    },
  });
}

// =============================================================================
// Rider assignment
// =============================================================================

export type DeliveryAssignmentResult = {
  delivery: {
    id: string;
    orderId: string;
    organizationId: string;
    riderId: string | null;
    state: DeliveryState;
  };
};

export function useAssignRider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; riderUserId: string }) =>
      await requestJson<DeliveryAssignmentResult>(`/api/orders/${input.orderId}/assign-rider`, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ riderUserId: input.riderUserId }),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.orders.detail(variables.orderId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.delivery.byOrder(variables.orderId),
        }),
      ]);
    },
  });
}

export function useCancelDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { deliveryId: string; orderId: string; reason?: string }) =>
      await requestJson<{ delivery: DeliverySummary }>(
        `/api/deliveries/${input.deliveryId}/cancel`,
        {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify(input.reason ? { reason: input.reason } : {}),
        },
      ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.orders.detail(variables.orderId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.delivery.byOrder(variables.orderId),
        }),
      ]);
    },
  });
}
